import { useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import type { GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ConfirmDialog, DateRangeFilter, ServerDataGrid, formatCurrency, formatDate, useDataGridState,
} from "@safrater/ui";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

interface LicenseRow {
  id: string;
  status: string;
  validUntil: string | null;
  trialEndsAt: string | null;
  module: { id: string; name: string; key: string };
  institution: { id: string; name: string; cnpj: string };
  licenseType: { id: string; kind: "free" | "trial" | "monthly"; name: string; monthlyPrice: string | null };
  createdAt: string;
}

interface InstitutionLite { id: string; name: string }
interface ModuleLite { id: string; name: string; key: string; licenseTypes: { id: string; name: string; kind: string }[] }

const KIND_LABEL: Record<string, string> = { free: "Gratuita", trial: "Trial", monthly: "Mensal" };

export function LicensesPage() {
  const state = useDataGridState();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMaster = !!user?.isMasterAdmin;
  const [grantOpen, setGrantOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<LicenseRow | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["licenses", state.queryParams],
    queryFn: async () => (await api.get<{ data: LicenseRow[]; total: number }>("/licenses", { params: state.queryParams })).data,
    placeholderData: (prev) => prev,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["licenses"] });
  const grantM = useMutation({
    mutationFn: (body: { institutionId: string; moduleId: string; licenseTypeId: string }) =>
      api.post("/licenses/grant", body),
    onSuccess: () => { invalidate(); setGrantOpen(false); },
  });
  const revokeM = useMutation({
    mutationFn: (id: string) => api.post(`/licenses/${id}/revoke`),
    onSuccess: () => { invalidate(); setConfirmRevoke(null); },
  });

  const columns: GridColDef<LicenseRow>[] = [
    { field: "institution", headerName: "Instituição", flex: 1, minWidth: 240, sortable: false, valueGetter: (_v, row) => row.institution.name },
    { field: "module", headerName: "Módulo", flex: 1, minWidth: 180, sortable: false, valueGetter: (_v, row) => row.module.name },
    {
      field: "licenseType", headerName: "Tipo", width: 200, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip size="small" label={KIND_LABEL[row.licenseType.kind]} color={row.licenseType.kind === "monthly" ? "primary" : "default"} />
          {row.licenseType.monthlyPrice && <Typography variant="caption">{formatCurrency(Number(row.licenseType.monthlyPrice))}</Typography>}
        </Stack>
      ),
    },
    { field: "validUntil", headerName: "Válida até", width: 140, type: "date", valueGetter: (v) => (v ? new Date(v as string) : null), valueFormatter: (v) => (v ? formatDate((v as Date).toISOString()) : "—") },
    {
      field: "status", headerName: "Status", width: 120,
      renderCell: ({ value }) => (
        <Chip size="small" label={value as string} color={value === "active" ? "success" : value === "expired" ? "error" : "warning"} />
      ),
    },
    ...(isMaster ? [{
      field: "_actions", headerName: "Ações", width: 110, sortable: false, filterable: false,
      renderCell: ({ row }: { row: LicenseRow }) => (
        row.status === "active" ? (
          <Tooltip title="Revogar"><IconButton size="small" color="error" onClick={() => setConfirmRevoke(row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        ) : null
      ),
    } as GridColDef<LicenseRow>] : []),
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4">Licenças</Typography>
        {isMaster && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGrantOpen(true)}>
            Conceder licença
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Licenças são concedidas à instituição. Todos os usuários ativos da instituição com
        tipo de usuário apropriado herdam o acesso ao módulo.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <DateRangeFilter
          value={state.dateRange ?? { start: null, end: null }}
          onChange={state.setDateRange}
          startLabel="Criada a partir de"
          endLabel="Até"
        />
      </Box>
      <Box sx={{ height: 640 }}>
        <ServerDataGrid<LicenseRow>
          gridId="central.licenses"
          userScope={user?.sub}
          rows={data?.data ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          paginationModel={state.pagination}
          onPaginationModelChange={state.setPagination}
          sortModel={state.sort}
          onSortModelChange={state.setSort}
          filterModel={state.filter}
          onFilterModelChange={state.setFilter}
        />
      </Box>

      {grantOpen && (
        <GrantLicenseDialog
          key="grant"
          onClose={() => setGrantOpen(false)}
          onSubmit={(b) => grantM.mutate(b)}
          submitting={grantM.isPending}
        />
      )}

      <ConfirmDialog
        open={!!confirmRevoke}
        title="Revogar licença"
        description={`Revogar a licença de "${confirmRevoke?.institution.name}" para "${confirmRevoke?.module.name}"?`}
        confirmLabel="Revogar"
        severity="error"
        loading={revokeM.isPending}
        onConfirm={() => { if (confirmRevoke) revokeM.mutate(confirmRevoke.id); }}
        onCancel={() => setConfirmRevoke(null)}
      />
    </Box>
  );
}

export function GrantLicenseDialog({
  onClose, onSubmit, submitting, lockedInstitutionId,
}: {
  onClose: () => void;
  onSubmit: (b: { institutionId: string; moduleId: string; licenseTypeId: string }) => void;
  submitting: boolean;
  /** Quando fornecido, a licença é concedida a esta instituição (oculta o seletor). */
  lockedInstitutionId?: string;
}) {
  const [institutionId, setInstitutionId] = useState(lockedInstitutionId ?? "");
  const [moduleId, setModuleId] = useState("");
  const [licenseTypeId, setLicenseTypeId] = useState("");

  const institutionsQ = useQuery({
    queryKey: ["institutions-lite"],
    queryFn: async () => (await api.get<{ data: InstitutionLite[] }>("/institutions", { params: { page_size: 200 } })).data.data,
    enabled: !lockedInstitutionId,
  });
  const modulesQ = useQuery({
    queryKey: ["modules-lite"],
    queryFn: async () => (await api.get<ModuleLite[]>("/modules")).data,
  });

  const mod = modulesQ.data?.find((m) => m.id === moduleId);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth maxWidth="sm"
      slotProps={{ paper: { component: "form", onSubmit: (e: React.FormEvent) => {
        e.preventDefault();
        if (institutionId && moduleId && licenseTypeId) {
          onSubmit({ institutionId, moduleId, licenseTypeId });
        }
      } } }}
    >
      <DialogTitle>Conceder licença</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!lockedInstitutionId && (
          <TextField
            select label="Instituição" required value={institutionId}
            onChange={(e) => setInstitutionId(e.target.value)} fullWidth
          >
            {(institutionsQ.data ?? []).map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
          </TextField>
          )}
          <TextField
            select label="Módulo" required value={moduleId}
            onChange={(e) => { setModuleId(e.target.value); setLicenseTypeId(""); }}
            fullWidth
          >
            {(modulesQ.data ?? []).map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
          <TextField
            select label="Tipo de licença" required value={licenseTypeId}
            onChange={(e) => setLicenseTypeId(e.target.value)}
            disabled={!mod} fullWidth
          >
            {(mod?.licenseTypes ?? []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={submitting || !institutionId || !moduleId || !licenseTypeId}>
          Conceder
        </Button>
      </DialogActions>
    </Dialog>
  );
}
